import { inject, injectable } from 'inversify';
import { IApiService, EventStatFilter, IStatService, EventOrderBy, OrderByMode, EventStatExportFilter } from '@/interfaces/Services';
import { auth } from '@/store/modules';
import { EventStatModel } from '@/models/stat';
import DependencyIdentifiers from '@/ioc/DependencyIdentifiers';

export type PagedEventStatResponse = {
  data: EventStatModel[],
  meta: { total: number; }
};

@injectable()
export default class StatService implements IStatService {
  private _api: IApiService;

  constructor(@inject(DependencyIdentifiers.ApiService) api: IApiService) {
    this._api = api;
  }

  async getManyEvent(
    page: number = 1,
    size: number = 20,
    filter: EventStatFilter = {},
  ): Promise<[EventStatModel[], number]> {
    let query = `/stats/event?size=${size}&page=${page}`;
    query += this.getFilterQueryParams(filter);

    const res = await this._api.get<PagedEventStatResponse>(query);
    return [res.data, res.meta.total];
  }

  async getExport(
    page: number = 1,
    size: number = 20,
    filter: EventStatFilter = {},
    format: string = 'csv',
    centerId?: string,
  ): Promise<string> {
    let query = `/stats/event/export?format=${format}&size=${size}&page=${page}`;
    query += this.getExportFilterParams(filter);

    const result = await this._api.request<{format: string, data: string, mime: string}>(query, { method: 'GET', overrideCenterId: centerId });
    return result.data;
  }

  private getFilterQueryParams(filter: EventStatFilter): string {
    let query = '';
    if (!!filter.doctorIds) {
      query += `&filter[doctorIds]=${filter.doctorIds.join(',')}`;
    }
    if (!!filter.patientIds) {
      query += `&filter[patientIds]=${filter.patientIds.join(',')}`;
    }
    if (!!filter.eventTypes) {
      query += `&filter[eventTypes]=${filter.eventTypes.join(',')}`;
    }
    if (!!filter.eventStatuses) {
      query += `&filter[eventStatuses]=${filter.eventStatuses.join(',')}`;
    }
    if (!!filter.from) { query += `&filter[from]=${filter.from}`; }
    if (!!filter.until) { query += `&filter[until]=${filter.until}`; }
    if (!!filter.socialSecurityNo) {
      query += `&filter[socialSecurityNo]=${filter.socialSecurityNo}`;
    }
    if (auth.exportLock !== 'pharmacy') {
      query = `${query}` + `&filter[centerId]=${auth.currentCenterId}`;
    }

    return query;
  }

  private getExportFilterParams(filter: EventStatExportFilter): string {
    let query = this.getFilterQueryParams(filter);
    if (!!filter.fields) {
      query += `&filter[fields]=${filter.fields.join(',')}`;
    }

    return query;
  }
}
